import { Component, ViewChild, OnInit } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { Router } from '@angular/router';
import { FeedbackComponent } from 'src/app/general-components/feedback/feedback.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorResponse } from 'src/app/interfaces/bad-request';
import { concat, EMPTY, Observable } from 'rxjs';
import { ReservedSpecialSku } from '../reserved-special-sku';
import { SpecialsService } from '../specials.service';
import { EmptyStats, Stats } from '../suppliers/supplier';
import { EmptyTUSpecial, TUSpecial } from './tu-special';
import { NewTUSpecialComponent } from './new-tu-special/new-tu-special.component';
import { TUSpecialsTableComponent } from './tu-specials-table/tu-specials-table.component';
import { TUCombinedData, TUSpecialsService } from './tu-specials.service';

@Component({
  selector: 'app-tu-specials',
  templateUrl: './tu-specials.component.html',
  styleUrls: ['./tu-specials.component.scss'],
})
export class TUSpecialsComponent implements OnInit {
  @ViewChild(NewTUSpecialComponent) newTUSpecialComponent!: NewTUSpecialComponent;
  @ViewChild(TUSpecialsTableComponent) TUSpecialsTableComponent!: TUSpecialsTableComponent;
  @ViewChild(FeedbackComponent) feedbackComponent!: FeedbackComponent;
  specials: TUSpecial[] = [];
  stats: Stats = EmptyStats();
  newSpecial: TUSpecial = EmptyTUSpecial();
  failedSpecial: TUSpecial | undefined;
  keepInputs: boolean = false;

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: 'Er is iets misgegaan sorry',
    closeButton: false,
  };
  newSpecialFeedbackIdentifier = 'newSpecialFeedback';
  generalFeedbackIdentifier = 'generalFeedback';

  reservedSpecialSku: ReservedSpecialSku | undefined;

  isLoading: boolean = false;

  constructor(
    private TUservice: TUSpecialsService,
    private specialsService: SpecialsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Sequential on purpose: Firefox aborts parallel requests to the VPN-only API hosts,
    // see "Parallel requests to private API hosts" in AGENTS.md.
    concat(this.getReservedSpecialSku(), this.getTUSpecials()).subscribe();
  }

  getTUSpecials(): Observable<unknown> {
    this.isLoading = true;
    return this.TUservice.getCombinedData().pipe(
      tap((combinedData: TUCombinedData) => {
        this.specials = combinedData.specials;
        this.stats = combinedData.stats;
        this.isLoading = false;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er is iets foutgegaan bij het ophalen van de TU specials',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  getReservedSpecialSku(): Observable<unknown> {
    this.isLoading = true;
    return this.specialsService.getReservedSpecialSku().pipe(
      tap((reservedSpecialSku: ReservedSpecialSku) => {
        this.reservedSpecialSku = reservedSpecialSku;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er kon geen special gereserveerd worden, probeer het later nog een keer',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  setKeepInput(keepInput: boolean) {
    this.keepInputs = keepInput;
  }

  createSpecial(special: TUSpecial) {
    this.isLoading = true;
    this.TUservice.createTUSpecial(special).subscribe({
      next: (newSpecial: TUSpecial) => {
        this.specials.push(newSpecial);

        this.TUSpecialsTableComponent.sortByField('creationDate', false);
        this.updateFeedback(
          this.newSpecialFeedbackIdentifier,
          FeedbackType.success,
          true,
          `De special ${newSpecial.sku} is aangemaakt!`,
          undefined,
          false
        );
        this.feedbackComponent.fadeOut(2000);
        this.getReservedSpecialSku().subscribe();
        if (!this.keepInputs) {
          this.newTUSpecialComponent.clearSpecial();
        } else {
          this.newTUSpecialComponent.clearUniqueFields();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.failedSpecial = special;
        console.error(error.error);
        if (error.status == 400) {
          const badRequestError = error.error as ErrorResponse;
          const errorException = badRequestError.exception;
          if (errorException?.includes('DoubleDescriptionException')) {
            const SKUs = this.getSKUsFromError(errorException);
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,
              FeedbackType.error,
              true,
              `Er bestaat al een special met de omschrijving "${special.description}": ${SKUs}`,
              'Aanpassen en opnieuw proberen',
              true
            );
          } else {
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,
              FeedbackType.error,
              true,
              'Er klopte *iets* niet aan de special',
              'Aanpassen en opnieuw proberen',
              true
            );
          }
        } else {
          this.updateFeedback(
            this.newSpecialFeedbackIdentifier,
            FeedbackType.error,
            true,
            'Er is iets fout gegaan bij aanmaken van de special',
            undefined,
            true
          );
        }

        this.isLoading = false;
      },
    });
  }

  getSKUsFromError(errorException: string) {
    const parts = errorException.match(/\[(.*?)\]/);
    return parts ? parts[1] : 'Niet beschikbaar';
  }

  retryFailedSpecial() {
    if (this.failedSpecial) {
      this.feedbackSettings.show = false;
      this.failedSpecial.creationDate = '';

      this.newTUSpecialComponent.retryFailedSpecial(this.failedSpecial);
    }
  }

  updateFeedback(
    identifier: string,
    type: FeedbackType,
    show: boolean,
    message: string,
    buttonText: string | undefined,
    closeButton: boolean
  ) {
    this.feedbackSettings.identifier = identifier;
    this.feedbackSettings.type = type;
    this.feedbackSettings.show = show;
    this.feedbackSettings.message = message;
    this.feedbackSettings.buttonText = buttonText;
    this.feedbackSettings.closeButton = closeButton;
  }

  handleFeedbackOutput(identifier: string) {
    if (identifier == this.newSpecialFeedbackIdentifier) {
      this.retryFailedSpecial();
    } else {
      this.returnHome();
    }
  }
  returnHome() {
    void this.router.navigate(['home']);
  }

  feedbackType() {
    return FeedbackType;
  }
}
